// FILE: src/pages/ZReportDetailPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { formatMoney } from "../utils/formatMoney";

function ZReportDetailPage() {
  const { zReportId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [zReport, setZReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadZReport(zReportId);
  }, [zReportId]);

  async function loadZReport(id) {
    try {
      setLoading(true);
      setError(null);

      // GET /zreports/:id
      const res = await API.get(`/zreports/${id}`);
      setZReport(res.data);
    } catch (err) {
      console.error("Failed to fetch ZReport detail:", err);
      setError("Failed to fetch details");
    } finally {
      setLoading(false);
    }
  }

  // Helper to format date as YYYY-MM-DD
  const formatDate = (dateString) => {
    return dateString ? dateString.split("T")[0] : "";
  };

  // Color-coded Chip for status
  const getStatusChip = () => {
    if (!zReport || !zReport.status) return null;
    switch (zReport.status) {
      case "checked":
        return <Chip label="Checked" color="success" />;
      case "pending":
        return <Chip label="Pending" color="warning" />;
      default:
        return <Chip label={zReport.status} color="default" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (!zReport) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <Typography>No data found</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">Z Report Details</Typography>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            {/* Account Info */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Account
              </Typography>
              <Typography variant="body1">
                {zReport.account?.accountName} ({zReport.account?.accountNo})
              </Typography>
            </Grid>

            {/* Expected Date */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Expected Date
              </Typography>
              <Typography variant="body1">
                {formatDate(zReport.expectedDate)}
              </Typography>
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Category
              </Typography>
              <Typography variant="body1">
                {zReport.category?.name || "N/A"}
              </Typography>
            </Grid>

            {/* Target Cash */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Target Cash
              </Typography>
              <Typography variant="body1">
                {formatMoney(zReport.targetCash || 0, zReport.currency)}
              </Typography>
            </Grid>

            {/* Declared Cash */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Declared Cash
              </Typography>
              <Typography variant="body1">
                {formatMoney(zReport.declaredCash || 0, zReport.currency)}
              </Typography>
            </Grid>

            {/* Till Variance */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Till Variance
              </Typography>
              <Typography variant="body1">
                {formatMoney(zReport.tillVariance || 0, zReport.currency)}
              </Typography>
            </Grid>

            {/* Expected Amount */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Expected Amount
              </Typography>
              <Typography variant="body1">
                {formatMoney(zReport.expectedAmount || 0, zReport.currency)}
              </Typography>
            </Grid>

            {/* Actual Date */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Actual Date
              </Typography>
              <Typography variant="body1">
                {formatDate(zReport.actualDate)}
              </Typography>
            </Grid>

            {/* Actual Amount */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Actual Amount
              </Typography>
              <Typography variant="body1">
                {formatMoney(zReport.actualAmount || 0, zReport.currency)}
              </Typography>
            </Grid>

            {/* Variance */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                c2bVariance
              </Typography>
              <Typography variant="body1">
                {formatMoney(zReport.c2bvariance || 0, zReport.currency)}
              </Typography>
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              {getStatusChip()}
            </Grid>

            {/* Responsible User */}
            {zReport.responsibleUser && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Confirmed by
                </Typography>
                <Typography variant="body1">
                  {/* 
                    If you populated 'responsibleUser' with "displayName" & "username",
                    show whichever is available (displayName first).
                  */}
                  {zReport.responsibleUser.displayName ||
                    zReport.responsibleUser.username ||
                    "N/A"}
                </Typography>
              </Grid>
            )}

            {/* Comments (if any) */}
            {zReport.comments && zReport.comments.trim() !== "" && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Comments
                </Typography>
                <Typography variant="body1">{zReport.comments}</Typography>
              </Grid>
            )}

            {/* Notes (if any) */}
            {zReport.notes && zReport.notes.trim() !== "" && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body1">{zReport.notes}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
}

export default ZReportDetailPage;
